// How players pay (ADR-0058; spec/13 §13.1, spec/06 §3–§5): the venue's essential setup step `price`, opened in place
// from its Setup page (and from the Floor's "Finish setup" banner) as a centred dialog. The three ways players pay for
// play time, each a card with one line on what it is, one on when to use it, where it stands now, and what changes it:
//
//   Passes        the essential: with none on sale, "Create your first pass", prefilled — 1 hour, 60 minutes, an hour
//                 at the venue's cheapest class rate (`@clavian/domain` `starterPassPrice`), every class here, sold at
//                 the counter and on the PCs, usable 30 days. A venue with no station class yet names its first one
//                 here, made with the pass. `POST …/passes/products` (`passes:manage`) and, for the class,
//                 `POST …/station-classes` (`stations:manage`).
//   Pay as you go off for a venue made since 2026-10-12: its rate prefilled, so turning it on is one switch, which
//                 saves `sessions.payAsYouGoEnabled` and `sessions.paygHourlyRate` at the venue together. Turning it
//                 off asks first, here in the card (never a second dialog on top).
//   Wallet        the organisation's `wallet.enabled` (off until turned on, for every venue) with its top-up limits,
//                 and the venue's `sessions.walletFundsPlay` (off by default), which needs the wallet and pay as you
//                 go on. Turning the wallet off asks first.
//
// Every value is the settings registry's, read and written through the settings api with its ETag, under the member's
// own permissions — the server decides what they may change (`editable`), and the card says so when they may not. Each
// card ends with where it is changed later. Money is integer minor units of the venue's currency throughout; the
// fields show and take the major unit (`org/money.ts`).

import type { CreatePassProductRequest, PassProduct, SettingEntry, SettingsView, StationClass } from '@clavian/api-client';
import { STARTER_PASS_CHANNELS, STARTER_PASS_MINUTES, STARTER_PASS_VALIDITY_DAYS, starterPassPrice } from '@clavian/domain/setup';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, Dialog, InfoTip, Skeleton, Switch, TextField, useToast } from '@clavian/ui-web';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useId, useMemo, useState, type FormEvent, type ReactNode } from 'react';

import { useApi } from '../api/client';
import { problemCode, PROBLEM } from '../api/problems';
import { useAccess } from '../api/queries';
import type { Versioned } from '../api/versioned';
import { moneyFrom, minorToMajor } from '../org/money';
import { useCreatePassProduct, usePassProducts } from '../org/passes/queries';
import { useRateCard } from '../org/pricing/queries';
import { writeSettings, type SettingsScope } from '../settings/registry-source';
import { settingsKey, useSettingsView } from '../settings/SettingsPage';
import { useConsoleScope, useCurrentVenue } from '../shell/scope';
import { useCreateStationClass, useStationClasses } from '../stations/queries';
import { Notice } from '../ui';
import { invalidateSetup } from './queries';

import './setup.css';

const PAYG = 'sessions.payAsYouGoEnabled';
const PAYG_RATE = 'sessions.paygHourlyRate';
const WALLET = 'wallet.enabled';
const WALLET_FUNDS_PLAY = 'sessions.walletFundsPlay';

const PASSES_MANAGE = 'passes:manage';
const STATIONS_MANAGE = 'stations:manage';
const PRICING_READ = 'pricing:read';

export function HowPlayersPayDialog({ open, onOpenChange }: { readonly open: boolean; readonly onOpenChange: (open: boolean) => void }): ReactNode {
  const { t } = useTranslation('console');
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={t('setup.pay.title')}
      description={t('setup.pay.intro')}
      footer={
        <Button variant="secondary" onClick={() => onOpenChange(false)}>
          {t('setup.pay.done')}
        </Button>
      }
    >
      {open ? <HowPlayersPay /> : null}
    </Dialog>
  );
}

/** A pass on sale at this venue, as the server counts one for the step (`passesOnSaleAt`). */
function onSaleAt(product: PassProduct, venueId: string): boolean {
  const version = product.version;
  const covers = version.scope.type !== 'venues' || (version.scope.venueIds ?? []).some((id) => id === venueId);
  return product.active && version.channels.length > 0 && version.price.amount > 0 && covers && (version.membersOnly ?? []).length === 0;
}

const entryOf = (view: SettingsView | undefined, key: string): SettingEntry | undefined => view?.settings.find((entry) => entry.key === key);
const numberOf = (entry: SettingEntry | undefined): number | undefined => (typeof entry?.value === 'number' ? entry.value : undefined);

function HowPlayersPay(): ReactNode {
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const scope = useConsoleScope();
  const access = useAccess({ venueId: venue.id });
  const ready = access.status === 'ready';
  const venueScope = useMemo<SettingsScope>(() => ({ kind: 'venue', organizationId: venue.organizationId, venueId: venue.id }), [venue.organizationId, venue.id]);
  const organizationScope = useMemo<SettingsScope>(() => ({ kind: 'organization', organizationId: venue.organizationId }), [venue.organizationId]);
  const venueSettings = useSettingsView(venueScope).query;
  const organizationSettings = useSettingsView(organizationScope).query;
  const passes = usePassProducts(venue.organizationId, venue.id);
  const classes = useStationClasses(venue.organizationId, venue.id);
  const rateCard = useRateCard(venue.organizationId, venue.id, ready && access.can(PRICING_READ));

  const settled = (query: { readonly data?: unknown; readonly isError: boolean }) => query.data !== undefined || query.isError;
  if (access.status === 'loading' || !settled(venueSettings) || !settled(passes) || !settled(classes) || (rateCard.fetchStatus === 'fetching' && rateCard.data === undefined)) {
    return (
      <div className="console-pay-ways" role="status" aria-busy="true" aria-label={t('setup.pay.loading')}>
        <Skeleton height="9rem" />
        <Skeleton height="9rem" />
        <Skeleton height="9rem" />
      </div>
    );
  }
  if (venueSettings.data === undefined) {
    return <Notice tone="danger" role="alert">{t('setup.pay.unavailable')}</Notice>;
  }

  const view = venueSettings.data;
  const currency = view.data.scope.currency ?? venue.currency ?? 'INR';
  const flat = numberOf(entryOf(view.data, PAYG_RATE)) ?? rateCard.data?.fallback.hourlyAmount.amount ?? 0;
  const lines = new Map((rateCard.data?.current?.rates ?? []).map((line) => [line.stationClassId as string, line.hourlyAmount.amount]));
  const venueClasses = classes.data ?? [];
  const price = starterPassPrice({ classHourlyRates: venueClasses.map((klass) => lines.get(klass.id) ?? flat), flatHourlyRate: flat });
  const paygOn = entryOf(view.data, PAYG)?.value !== false;
  const organizationName = scope.kind === 'venue' ? scope.organization?.name : scope.organization.name;
  const organizationSlug = scope.kind === 'venue' ? scope.organization?.slug : scope.organization.slug;

  return (
    <ol className="console-pay-ways" aria-label={t('setup.pay.waysLabel')}>
      <PassesCard
        venueId={venue.id}
        venueSlug={venue.slug}
        organizationId={venue.organizationId}
        currency={currency}
        products={passes.data ?? []}
        classes={venueClasses}
        price={price}
        canManage={ready && access.can(PASSES_MANAGE)}
        canAddClass={ready && access.can(STATIONS_MANAGE)}
      />
      <PaygCard scope={venueScope} view={view} currency={currency} venueSlug={venue.slug} />
      <WalletCard
        venueView={view}
        venueScope={venueScope}
        organizationView={organizationSettings.data}
        organizationScope={organizationScope}
        currency={currency}
        paygOn={paygOn}
        organizationName={organizationName}
        later={
          organizationSlug === undefined
            ? { to: '/v/$venueSlug/settings/$group', params: { venueSlug: venue.slug, group: 'money' } }
            : { to: '/o/$orgSlug/settings/$group', params: { orgSlug: organizationSlug, group: 'money' }, hash: WALLET }
        }
      />
    </ol>
  );
}

// -------------------------------------------------------------------------------------------------
// One card per way
// -------------------------------------------------------------------------------------------------

interface LaterLink {
  readonly to: string;
  readonly params: Readonly<Record<string, string>>;
  readonly hash?: string;
}

function WayCard({
  way,
  title,
  state,
  stateTone,
  recommended,
  what,
  when,
  summary,
  defaultOpen = false,
  later,
  children,
}: {
  readonly way: 'passes' | 'payg' | 'wallet';
  readonly title: string;
  readonly state: string;
  readonly stateTone: 'ok' | 'neutral';
  readonly recommended?: boolean;
  readonly what: string;
  readonly when: string;
  /** Where it stands, in one line or a short list, always in view. */
  readonly summary: ReactNode;
  /** Open the details from the start (the first pass, which the step waits on). */
  readonly defaultOpen?: boolean;
  readonly later: { readonly label: string; readonly link: LaterLink };
  readonly children: ReactNode;
}): ReactNode {
  const { t } = useTranslation('console');
  const headingId = `console-pay-way-${way}`;
  const bodyId = useId();
  const [open, setOpen] = useState(defaultOpen);
  return (
    <li className="console-pay-way" data-way={way} data-open={open ? 'true' : 'false'}>
      <div className="console-pay-way__head">
        <h3 id={headingId} className="console-pay-way__title">
          {title}
          <InfoTip>{when}</InfoTip>
        </h3>
        <div className="console-pay-way__badges">
          {recommended === true ? <Badge tone="accent">{t('setup.pay.recommended')}</Badge> : null}
          <Badge tone={stateTone} dot>
            {state}
          </Badge>
        </div>
      </div>
      <p className="console-pay-way__what">{what}</p>
      <div className="console-pay-way__summary">{summary}</div>
      <Button
        variant={open ? 'ghost' : 'secondary'}
        size="sm"
        className="console-pay-way__edit"
        aria-expanded={open}
        aria-controls={bodyId}
        label={t(open ? 'setup.pay.closeNamed' : 'setup.pay.editNamed', { way: title })}
        onClick={() => setOpen(!open)}
      >
        {open ? t('setup.pay.close') : t('setup.pay.edit')}
      </Button>
      <div id={bodyId} className="console-pay-way__body" hidden={!open}>
        {children}
        <Link className="console-pay-way__later" to={later.link.to} params={later.link.params} {...(later.link.hash === undefined ? {} : { hash: later.link.hash })}>
          {later.label}
        </Link>
      </div>
    </li>
  );
}

/** An off switch's question, asked in the card itself: what follows, Keep it on, Turn off. */
function ConfirmOff({
  title,
  body,
  keep,
  confirm,
  busy,
  onKeep,
  onConfirm,
}: {
  readonly title: string;
  readonly body: string;
  readonly keep: string;
  readonly confirm: string;
  readonly busy: boolean;
  readonly onKeep: () => void;
  readonly onConfirm: () => void;
}): ReactNode {
  return (
    <Notice
      tone="warn"
      role="alert"
      title={title}
      action={
        <>
          <Button variant="ghost" size="sm" onClick={onKeep}>
            {keep}
          </Button>
          <Button variant="danger" size="sm" busy={busy} onClick={onConfirm}>
            {confirm}
          </Button>
        </>
      }
    >
      <p className="console-pay-way__note">{body}</p>
    </Notice>
  );
}

/** Save settings at one scope with the ETag they were read at, then keep the fresh view; a stale write reads again. */
function useSaveSettings(scope: SettingsScope) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t } = useTranslation('console');
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState<string | undefined>(undefined);
  const save = async (view: Versioned<SettingsView>, changes: readonly { key: string; value: unknown }[]): Promise<boolean> => {
    setSaving(true);
    setFailed(undefined);
    try {
      const fresh = await writeSettings(api, scope, changes.map((change) => ({ key: change.key, value: change.value })), view.etag);
      queryClient.setQueryData(settingsKey(scope), fresh);
      return true;
    } catch (error) {
      const stale = problemCode(error) === PROBLEM.staleWrite;
      if (stale) void queryClient.invalidateQueries({ queryKey: settingsKey(scope) });
      setFailed(stale ? t('setup.pay.saveStale') : t('setup.pay.saveFailed'));
      return false;
    } finally {
      setSaving(false);
    }
  };
  return { save, saving, failed };
}

/** The switch's accessible name is the way's; it shows its state in words. */
function StateSwitch({
  name,
  on,
  disabled,
  onChange,
  describedBy,
}: {
  readonly name: string;
  readonly on: boolean;
  readonly disabled: boolean;
  readonly onChange: (next: boolean) => void;
  readonly describedBy?: string;
}): ReactNode {
  const { t } = useTranslation('console');
  return (
    <Switch
      label={
        <>
          <span className="clav-visually-hidden">{`${name} `}</span>
          {on ? t('setup.pay.on') : t('setup.pay.off')}
        </>
      }
      checked={on}
      disabled={disabled}
      onCheckedChange={onChange}
      {...(describedBy === undefined ? {} : { 'aria-describedby': describedBy })}
    />
  );
}

// -------------------------------------------------------------------------------------------------
// Passes
// -------------------------------------------------------------------------------------------------

function PassesCard({
  venueId,
  venueSlug,
  organizationId,
  currency,
  products,
  classes,
  price,
  canManage,
  canAddClass,
}: {
  readonly venueId: string;
  readonly venueSlug: string;
  readonly organizationId: string;
  readonly currency: string;
  readonly products: readonly PassProduct[];
  readonly classes: readonly StationClass[];
  readonly price: number;
  readonly canManage: boolean;
  readonly canAddClass: boolean;
}): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const onSale = products.filter((product) => onSaleAt(product, venueId));
  const shown = onSale.slice(0, 3);

  return (
    <WayCard
      way="passes"
      title={t('setup.pay.passes.title')}
      state={onSale.length > 0 ? t('setup.pay.passes.onSale', { count: onSale.length }) : t('setup.pay.passes.none')}
      stateTone={onSale.length > 0 ? 'ok' : 'neutral'}
      recommended
      what={t('setup.pay.passes.what')}
      when={t('setup.pay.passes.when')}
      defaultOpen={onSale.length === 0}
      summary={
        onSale.length > 0 ? (
          <ul className="console-pay-way__list" aria-label={t('setup.pay.passes.listLabel')}>
            {shown.map((product) => (
              <li key={product.id}>
                <span>{product.version.name}</span>
                <span className="console-num">{format.money(product.version.price)}</span>
              </li>
            ))}
            {onSale.length > shown.length ? <li className="console-subtle">{t('setup.pay.passes.more', { count: onSale.length - shown.length })}</li> : null}
          </ul>
        ) : (
          <p className="console-subtle">{t('setup.pay.passes.noneSummary')}</p>
        )
      }
      later={{ label: t('setup.pay.passes.later'), link: { to: '/v/$venueSlug/pricing/$tab', params: { venueSlug, tab: 'passes' } } }}
    >
      {onSale.length > 0 ? null : canManage && (classes.length > 0 || canAddClass) ? (
        <StarterPassForm venueId={venueId} organizationId={organizationId} currency={currency} classes={classes} price={price} />
      ) : (
        <Notice role="note">{t('setup.pay.passes.notPermitted')}</Notice>
      )}
    </WayCard>
  );
}

interface StarterForm {
  name: string;
  minutes: string;
  price: string;
  className: string;
}

function StarterPassForm({
  venueId,
  organizationId,
  currency,
  classes,
  price,
}: {
  readonly venueId: string;
  readonly organizationId: string;
  readonly currency: string;
  readonly classes: readonly StationClass[];
  readonly price: number;
}): ReactNode {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const queryClient = useQueryClient();
  const createPass = useCreatePassProduct(organizationId, venueId);
  const createClass = useCreateStationClass(organizationId, venueId);
  const needsClass = classes.length === 0;
  const [form, setForm] = useState<StarterForm>({
    name: t('setup.pay.passes.defaultName'),
    minutes: String(STARTER_PASS_MINUTES),
    price: minorToMajor(price, currency),
    className: t('setup.pay.passes.defaultClassName'),
  });
  const [errors, setErrors] = useState<Partial<Record<keyof StarterForm | 'form', string>>>({});
  const busy = createPass.isPending || createClass.isPending;

  const update = (key: keyof StarterForm, value: string) => setForm((current) => ({ ...current, [key]: value }));

  function validate(): Partial<Record<keyof StarterForm, string>> {
    const next: Partial<Record<keyof StarterForm, string>> = {};
    if (form.name.trim() === '' || form.name.trim().length > 40) next.name = t('setup.pay.passes.nameRequired');
    const minutes = Number(form.minutes);
    if (!Number.isInteger(minutes) || minutes < 15 || minutes > 43_200) next.minutes = t('setup.pay.passes.minutesRange');
    const money = moneyFrom(form.price, currency);
    if (money === null || money.amount <= 0) next.price = t('setup.pay.passes.priceInvalid');
    if (needsClass && (form.className.trim() === '' || form.className.trim().length > 60)) next.className = t('setup.pay.passes.classRequired');
    return next;
  }

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const found = validate();
    setErrors(found);
    const money = moneyFrom(form.price, currency);
    if (Object.keys(found).length > 0 || money === null) return;
    const name = form.name.trim();
    try {
      // A venue with no class yet names its first one here: the pass plays on it.
      const classIds = needsClass
        ? [(await createClass.mutateAsync({ name: form.className.trim(), colorToken: 'category-1', pricingMode: 'per_player' })).id]
        : classes.map((klass) => klass.id);
      const body: CreatePassProductRequest = {
        name,
        shortName: name.slice(0, 12).trim(),
        price: money,
        durationSeconds: Number(form.minutes) * 60,
        validity: { type: 'days_after_purchase', days: STARTER_PASS_VALIDITY_DAYS },
        stationClassIds: classIds as CreatePassProductRequest['stationClassIds'],
        scope: { type: 'venues', venueIds: [venueId as never] },
        windows: [],
        label: 'standard',
        channels: [...STARTER_PASS_CHANNELS],
        active: true,
        sortOrder: 0,
      };
      await createPass.mutateAsync(body);
      show({ tone: 'ok', title: t('setup.pay.passes.created', { name }) });
      // The step ticks off: read setup again (the page, the sidebar's count, the Floor's banner).
      void invalidateSetup(queryClient);
    } catch {
      setErrors({ form: t('setup.pay.passes.failed') });
    }
  }

  return (
    <form className="console-pay-way__form" onSubmit={(event) => void submit(event)} noValidate>
      <div>
        <p className="console-pay-way__form-title">{t('setup.pay.passes.createTitle')}</p>
        <p className="console-subtle console-pay-way__note">{t('setup.pay.passes.createHint')}</p>
      </div>
      {needsClass ? (
        <TextField
          label={t('setup.pay.passes.className')}
          description={t('setup.pay.passes.classNameHint')}
          required
          value={form.className}
          onChange={(event) => update('className', event.target.value)}
          error={errors.className}
        />
      ) : null}
      <div className="console-pay-way__fields">
        <TextField label={t('setup.pay.passes.name')} required value={form.name} onChange={(event) => update('name', event.target.value)} error={errors.name} />
        <TextField
          label={t('setup.pay.passes.minutes')}
          required
          numeric
          inputMode="numeric"
          value={form.minutes}
          onChange={(event) => update('minutes', event.target.value)}
          error={errors.minutes}
        />
        <TextField
          label={t('setup.pay.passes.price', { currency })}
          description={t('setup.pay.passes.priceHint')}
          required
          numeric
          inputMode="decimal"
          value={form.price}
          onChange={(event) => update('price', event.target.value)}
          error={errors.price}
        />
      </div>
      <p className="console-pay-way__note">
        {needsClass ? t('setup.pay.passes.newClass') : t('setup.pay.passes.everyClass', { classes: classes.map((klass) => klass.name).join(', ') })}{' '}
        {t('setup.pay.passes.sold', { days: STARTER_PASS_VALIDITY_DAYS })}
      </p>
      {errors.form === undefined ? null : (
        <p className="clav-field__error" role="alert">
          {errors.form}
        </p>
      )}
      <div className="console-form__actions">
        <Button type="submit" variant="primary" busy={busy}>
          {t('setup.pay.passes.create')}
        </Button>
      </div>
    </form>
  );
}

// -------------------------------------------------------------------------------------------------
// Pay as you go
// -------------------------------------------------------------------------------------------------

function PaygCard({
  scope,
  view,
  currency,
  venueSlug,
}: {
  readonly scope: SettingsScope;
  readonly view: Versioned<SettingsView>;
  readonly currency: string;
  readonly venueSlug: string;
}): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const { show } = useToast();
  const { save, saving, failed } = useSaveSettings(scope);
  const switchEntry = entryOf(view.data, PAYG);
  const rateEntry = entryOf(view.data, PAYG_RATE);
  const on = switchEntry?.value !== false;
  const rate = numberOf(rateEntry) ?? 0;
  const [typed, setTyped] = useState(() => minorToMajor(rate, currency));
  const [rateError, setRateError] = useState<string | undefined>(undefined);
  const [asking, setAsking] = useState(false);
  const editable = switchEntry?.editable === true;
  const hintId = 'console-pay-way-payg-hint';

  const turnOn = async () => {
    const money = moneyFrom(typed, currency);
    if (money === null || money.amount <= 0) {
      setRateError(t('setup.pay.payg.rateInvalid'));
      return;
    }
    setRateError(undefined);
    // The rate typed here goes with the switch: one save, so pay as you go never turns on at a rate nobody saw.
    const changes = [{ key: PAYG, value: true }, ...(rateEntry?.editable === true ? [{ key: PAYG_RATE, value: money.amount }] : [])];
    if (await save(view, changes)) show({ tone: 'ok', title: t('setup.pay.payg.turnedOn') });
  };
  const turnOff = async () => {
    if (await save(view, [{ key: PAYG, value: false }])) {
      setAsking(false);
      show({ tone: 'ok', title: t('setup.pay.payg.turnedOff') });
    }
  };

  return (
    <WayCard
      way="payg"
      title={t('setup.pay.payg.title')}
      state={on ? t('setup.pay.on') : t('setup.pay.off')}
      stateTone={on ? 'ok' : 'neutral'}
      what={t('setup.pay.payg.what')}
      when={t('setup.pay.payg.when')}
      summary={
        on ? (
          <p className="console-pay-way__figure console-num">{t('setup.pay.payg.rateNow', { rate: format.money({ amount: rate, currency }) })}</p>
        ) : (
          <p className="console-subtle">{t('setup.pay.payg.offSummary')}</p>
        )
      }
      later={{ label: t('setup.pay.payg.later'), link: { to: '/v/$venueSlug/pricing/$tab', params: { venueSlug, tab: 'payg' } } }}
    >
      {on ? null : (
        <TextField
          label={t('setup.pay.payg.rate', { currency })}
          description={t('setup.pay.payg.rateHint')}
          numeric
          inputMode="decimal"
          value={typed}
          disabled={!editable || rateEntry?.editable !== true}
          onChange={(event) => setTyped(event.target.value)}
          error={rateError}
        />
      )}
      {editable ? (
        <StateSwitch name={t('setup.pay.payg.switchLabel')} on={on} disabled={saving || asking} onChange={(next) => (next ? void turnOn() : setAsking(true))} describedBy={hintId} />
      ) : null}
      <p id={hintId} className="console-subtle console-pay-way__note">
        {editable ? (on ? t('setup.pay.payg.onHint') : t('setup.pay.payg.offHint')) : t('setup.pay.payg.readOnly')}
      </p>
      {asking ? (
        <ConfirmOff
          title={t('setup.pay.payg.confirmOff')}
          body={t('setup.pay.payg.confirmOffBody')}
          keep={t('setup.pay.payg.keepOn')}
          confirm={t('setup.pay.payg.turnOff')}
          busy={saving}
          onKeep={() => setAsking(false)}
          onConfirm={() => void turnOff()}
        />
      ) : null}
      {failed === undefined ? null : (
        <p className="clav-field__error" role="alert">
          {failed}
        </p>
      )}
    </WayCard>
  );
}

// -------------------------------------------------------------------------------------------------
// Wallet
// -------------------------------------------------------------------------------------------------

function WalletCard({
  venueView,
  venueScope,
  organizationView,
  organizationScope,
  currency,
  paygOn,
  organizationName,
  later,
}: {
  readonly venueView: Versioned<SettingsView>;
  readonly venueScope: SettingsScope;
  readonly organizationView: Versioned<SettingsView> | undefined;
  readonly organizationScope: SettingsScope;
  readonly currency: string;
  readonly paygOn: boolean;
  readonly organizationName: string | undefined;
  readonly later: LaterLink;
}): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const { show } = useToast();
  const organizationSave = useSaveSettings(organizationScope);
  const venueSave = useSaveSettings(venueScope);
  const [asking, setAsking] = useState(false);
  const walletEntry = entryOf(organizationView?.data, WALLET);
  const fundsEntry = entryOf(venueView.data, WALLET_FUNDS_PLAY);
  const walletOn = walletEntry?.value === true;
  const fundsOn = fundsEntry?.value === true;
  const money = (key: string) => {
    const amount = numberOf(entryOf(venueView.data, key));
    return amount === undefined ? undefined : format.money({ amount, currency });
  };
  const min = money('wallet.minTopupAmount');
  const max = money('wallet.maxTopupAmount');
  const balance = money('wallet.maxBalanceAmount');
  const canFund = walletOn && paygOn && fundsEntry?.editable === true;

  const turnWalletOn = async () => {
    if (organizationView !== undefined && (await organizationSave.save(organizationView, [{ key: WALLET, value: true }]))) show({ tone: 'ok', title: t('setup.pay.wallet.turnedOn') });
  };
  const turnWalletOff = async () => {
    if (organizationView !== undefined && (await organizationSave.save(organizationView, [{ key: WALLET, value: false }]))) {
      setAsking(false);
      show({ tone: 'ok', title: t('setup.pay.wallet.turnedOff') });
    }
  };
  const setFunds = async (next: boolean) => {
    if (await venueSave.save(venueView, [{ key: WALLET_FUNDS_PLAY, value: next }])) show({ tone: 'ok', title: next ? t('setup.pay.wallet.fundsOn') : t('setup.pay.wallet.fundsOff') });
  };
  const failed = organizationSave.failed ?? venueSave.failed;

  return (
    <WayCard
      way="wallet"
      title={t('setup.pay.wallet.title')}
      state={walletOn ? t('setup.pay.on') : t('setup.pay.off')}
      stateTone={walletOn ? 'ok' : 'neutral'}
      what={t('setup.pay.wallet.what')}
      when={t('setup.pay.wallet.when')}
      summary={
        min !== undefined && max !== undefined && balance !== undefined ? (
          <p className="console-subtle">{t('setup.pay.wallet.limits', { min, max, balance })}</p>
        ) : (
          <p className="console-subtle">{walletOn ? t('setup.pay.wallet.onSummary') : t('setup.pay.wallet.offSummary')}</p>
        )
      }
      later={{ label: t('setup.pay.wallet.later'), link: later }}
    >
      {walletEntry?.editable === true ? (
        <StateSwitch
          name={t('setup.pay.wallet.switchLabel')}
          on={walletOn}
          disabled={organizationSave.saving || asking}
          onChange={(next) => (next ? void turnWalletOn() : setAsking(true))}
          describedBy="console-pay-way-wallet-scope"
        />
      ) : null}
      <p id="console-pay-way-wallet-scope" className="console-subtle console-pay-way__note">
        {walletEntry?.editable === true
          ? organizationName === undefined
            ? t('setup.pay.wallet.scopeUnnamed')
            : t('setup.pay.wallet.scope', { organization: organizationName })
          : t('setup.pay.wallet.readOnly')}
      </p>
      {asking ? (
        <ConfirmOff
          title={t('setup.pay.wallet.confirmOff')}
          body={t('setup.pay.wallet.confirmOffBody')}
          keep={t('setup.pay.wallet.keepOn')}
          confirm={t('setup.pay.wallet.turnOff')}
          busy={organizationSave.saving}
          onKeep={() => setAsking(false)}
          onConfirm={() => void turnWalletOff()}
        />
      ) : null}
      {fundsEntry === undefined ? null : (
        <div className="console-pay-way__sub">
          <Switch
            label={t('setup.pay.wallet.fundsPlay')}
            checked={fundsOn}
            disabled={!canFund || venueSave.saving}
            onCheckedChange={(next) => void setFunds(next)}
            aria-describedby="console-pay-way-funds-hint"
          />
          <p id="console-pay-way-funds-hint" className="console-subtle console-pay-way__note">
            {walletOn && paygOn ? t('setup.pay.wallet.fundsPlayHint') : t('setup.pay.wallet.fundsPlayNeeds')}
          </p>
        </div>
      )}
      {failed === undefined ? null : (
        <p className="clav-field__error" role="alert">
          {failed}
        </p>
      )}
    </WayCard>
  );
}
